import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../context/LanguageContext";
import {
    Alert,
    Title,
    Center,
    Loader,
    Text,
    Paper,
    Group,
    Select,
    Modal,
    Button,
} from "@mantine/core";
import VisaTable from "../../../components/visa/VisaTable";
import VisaForm from "../../../components/visa/VisaForm";
import type { VisaApplication } from "../../../types/VisaApplication";
import HeroSectionV2 from "../../../components/HeroSectionV2";
import Billede1 from "../../../Images/Billlede-1.png";

interface EventItem {
    EventID?: number;
    eventName?: string;
    EventName?: string;
    eventId?: number;
    eventID?: number;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ALLOWED_TYPES = ["image/png", "image/jpeg", "application/pdf"];

const VisaPage: React.FC = () => {
    const { language } = useLanguage();
    const apiUrl = process.env.REACT_APP_API_URL;

    const [events, setEvents] = useState<EventItem[]>([]);
    const [selectedEventId, setSelectedEventId] = useState<string | null>("all");
    const [appliedEventId, setAppliedEventId] = useState<string | null>("all");

    const [data, setData] = useState<VisaApplication[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [editOpen, setEditOpen] = useState(false);
    const [editing, setEditing] = useState<VisaApplication | null>(null);
    const [saving, setSaving] = useState(false);

    const [file, setFile] = useState<File | null>(null);

    const eventOptions = [
        { value: "all", label: language === "da" ? "Alle" : "All" },
        ...events
            .map((e: EventItem) => {
                const id = e.eventId ?? e.eventID ?? e.EventID;
                const name = e.eventName ?? e.EventName ?? "Unnamed event";
                return { value: id ? String(id) : "", label: name };
            })
            .filter((o) => o.value !== ""),
    ];

    const startEdit = (row: VisaApplication) => {
        setEditing({ ...row });
        setFile(null);
        setError(null);
        setEditOpen(true);
    };

    const loadEvents = async () => {
        if (!apiUrl) throw new Error("REACT_APP_API_URL mangler");
        const res = await fetch(`${apiUrl}/api/events`);
        if (!res.ok) throw new Error("Failed to fetch events");
        setEvents(await res.json());
    };

    const loadVisa = async (eventIdToUse?: string | null) => {
        if (!apiUrl) throw new Error("REACT_APP_API_URL mangler");

        const id = eventIdToUse ?? "all";
        const qs = id && id !== "all" ? `?eventId=${id}` : "";
        const res = await fetch(`${apiUrl}/api/visaapplications${qs}`);
        if (!res.ok) throw new Error("Failed to fetch visa applications");
        setData(await res.json());
    };

    const saveEdit = async () => {
        if (!editing) return;

        if (file) {
            if (file.size > MAX_FILE_SIZE) {
                setError("Filen er for stor. Maks størrelse er 10 MB.");
                return;
            }

            if (!ALLOWED_TYPES.includes(file.type)) {
                setError("Kun PNG, JPG eller PDF filer er tilladt.");
                return;
            }
        }

        try {
            setSaving(true);
            setError(null);

            const formData = new FormData();
            formData.append("EventIDFK", String(editing.eventIDFK));
            formData.append("FirstName", editing.firstName ?? "");
            formData.append("MiddleName", editing.middleName ?? "");
            formData.append("LastName", editing.lastName ?? "");
            formData.append("Nationality", editing.nationality ?? "");
            formData.append("PassportNo", editing.passportNo ?? "");
            formData.append("PlaceOfBirth", editing.placeOfBirth ?? "");
            formData.append("CityToApply", editing.cityToApply ?? "");
            formData.append("Email", editing.email ?? "");
            formData.append("Sent", String(!!editing.sent));

            if (editing.dateOfBirth) {
                formData.append("DateOfBirth", editing.dateOfBirth);
            }

            if (editing.expiryDate) {
                formData.append("ExpiryDate", editing.expiryDate);
            }

            if (editing.issueDate) {
                formData.append("IssueDate", editing.issueDate);
            }

            if (file) {
                formData.append("InvitationLetterFile", file);
            }

            const res = await fetch(`${apiUrl}/api/visaapplications/${editing.id}`, {
                method: "PUT",
                body: formData,
            });

            if (!res.ok) {
                let message = `Fejl ved gemning (${res.status})`;

                if (res.status === 413) {
                    message = "Filen er for stor. Vælg en mindre fil.";
                } else {
                    try {
                        const text = await res.text();
                        if (text) {
                            message = text;
                        }
                    } catch {
                        // ignore
                    }
                }

                throw new Error(message);
            }

            setEditOpen(false);
            setEditing(null);
            setFile(null);

            await loadVisa(appliedEventId);
        } catch (e: any) {
            const msg =
                e?.message?.includes("Failed to fetch")
                    ? "Upload fejlede. Det kan skyldes at filen er for stor eller at serveren afviser requesten."
                    : e?.message ?? "Ukendt fejl";

            setError(msg);
        } finally {
            setSaving(false);
        }
    };

    const deleteVisa = async (id: number) => {
        if (!window.confirm("Delete this visa?")) return;

        const res = await fetch(`${apiUrl}/api/visaapplications/${id}`, {
            method: "DELETE",
        });
        if (!res.ok) throw new Error(await res.text());

        await loadVisa(appliedEventId);
    };

    useEffect(() => {
        const init = async () => {
            try {
                setLoading(true);
                setError(null);
                await loadEvents();
                await loadVisa("all");
            } catch (e: any) {
                setError(e.message ?? "Error");
            } finally {
                setLoading(false);
            }
        };
        init();
    }, []);

    useEffect(() => {
        if (loading) return;

        const id = selectedEventId ?? "all";
        setAppliedEventId(id);
        loadVisa(id).catch((e: any) => setError(e.message ?? "Error"));
    }, [selectedEventId, loading]);

    return (
        <div>
            <HeroSectionV2
                titleEn="Team booking"
                titleDa="Team booking"
                descriptionEn="Visa"
                descriptionDa="Visrum"
                imageSrc={Billede1}
                badgeText="TEAM"
            />

            <Center my="md">
                <Title order={1}>Visum</Title>
            </Center>

            {error && (
                <Center mb="md">
                    <Alert color="red" variant="light" title="Fejl" w="min(700px, 95vw)">
                        {error}
                    </Alert>
                </Center>
            )}

            {loading ? (
                <Center mt="lg" style={{ flexDirection: "column" }}>
                    <Loader color="red" size="lg" />
                    <Text mt="sm">{language === "da" ? "Indlæser..." : "Loading..."}</Text>
                </Center>
            ) : (
                <Center my="md" style={{ flexDirection: "column", gap: 16 }}>
                    <Paper withBorder p="md" style={{ width: "min(600px, 98vw)" }}>
                        <Group>
                            <Select
                                label="Event"
                                placeholder={language === "da" ? "Vælg event" : "Select event"}
                                data={eventOptions}
                                value={selectedEventId}
                                onChange={setSelectedEventId}
                                style={{ flex: 1 }}
                                searchable
                                clearable={false}
                            />
                        </Group>
                    </Paper>

                    <VisaTable
                        title="Visa requests"
                        data={data}
                        mode="admin"
                        onEdit={startEdit}
                        onDelete={deleteVisa}
                    />
                </Center>
            )}

            <Modal
                opened={editOpen}
                onClose={() => {
                    setEditOpen(false);
                    setFile(null);
                    setError(null);
                }}
                title="Edit visa"
                size="lg"
                centered
                styles={{
                    body: { maxHeight: "70vh", overflowY: "auto" },
                }}
            >
                {editing && (
                    <>
                        <VisaForm
                            value={editing}
                            onChange={(next) => setEditing(next)}
                            mode="admin-edit"
                            file={file}
                            setFile={setFile}
                        />

                        <Group justify="flex-end" mt="md">
                            <Button
                                variant="light"
                                color="gray"
                                onClick={() => {
                                    setEditOpen(false);
                                    setFile(null);
                                    setError(null);
                                }}
                            >
                                Cancel
                            </Button>
                            <Button loading={saving} onClick={saveEdit}>
                                Save
                            </Button>
                        </Group>
                    </>
                )}
            </Modal>
        </div>
    );
};

export default VisaPage;